import { ElementRef } from '@angular/core';

import { Interval } from 'app/types';

import Keyboard from './keyboard';
import { Keys } from './keys';
import Movement from './movement';
import { Player } from './player';
import { SceneManager } from './scene-manager';
import TouchController, { TouchDirection } from './touch-controller';

export default class Joystick {
  private keyboard: Keyboard;
  private touch: TouchController;
  private sent = 0;
  private moveInterval?: Interval;
  public movementEnabled = true;
  private player: Player | undefined;
  private movement: Movement;

  public constructor(
    elementRef: ElementRef,
    intervalTimeFrame: number,
    private sceneManager: SceneManager,
  ) {
    this.keyboard = Keyboard.getInstance();
    this.touch = new TouchController();
    this.setKeyboardEventListeners();
    this.setTouchEventListeners(elementRef);
    this.startMoveInterval(intervalTimeFrame);
    this.movement = Movement.getInstance(this.sceneManager);
  }

  /* set key board listeners */

  private setKeyboardEventListeners(): void {
    window.addEventListener('keyup', this.keyboard.onKeyUp.bind(this.keyboard));
    window.addEventListener('keydown', this.keyboard.onKeyDown.bind(this.keyboard));
  }

  /* set touch listeners on html element */

  private setTouchEventListeners(ref: ElementRef): void {
    const someElement = ref.nativeElement;
    someElement.addEventListener('touchstart', this.touch.touchesBegan.bind(this.touch), { passive: true });
    someElement.addEventListener('touchmove', this.touch.touchesMoved.bind(this.touch), { passive: true });
    someElement.addEventListener('touchcancel', this.touch.touchesCanceled.bind(this.touch), { passive: true });
    someElement.addEventListener('touchend', this.touch.touchesEnded.bind(this.touch), { passive: true });
  }

  /* move interval */

  private startMoveInterval(intervalTimeFrame: number): void {
    this.moveInterval = setInterval(() => {
      if (this.touch.touching) {
        this.checkTouchDirection();
      } else {
        this.checkKeyDown();
      }
    }, intervalTimeFrame);
  }

  private moveBy(moveBy: number): void {
    if (!this.movementEnabled) {
      return;
    }
    if (moveBy || this.sent !== moveBy) {
      this.movement.moveBy(moveBy);
      this.sent = moveBy;
      this.player = this.sceneManager.getPlayer();
      if (this.player && this.sceneManager.enableCenterViewport && moveBy !== 0) {
        this.player.setPlayerNotMovingTimeout();
      }
    }
  }

  private checkKeyDown(): void {
    let moveBy = 0;
    if (this.keyboard.isKeyPressed(Keys.KEY_W) || this.keyboard.isKeyPressed(Keys.KEY_UP)) {
      moveBy += Movement.MOVE_UP;
    }
    if (this.keyboard.isKeyPressed(Keys.KEY_S) || this.keyboard.isKeyPressed(Keys.KEY_DOWN)) {
      moveBy += Movement.MOVE_DOWN;
    }
    if (this.keyboard.isKeyPressed(Keys.KEY_A) || this.keyboard.isKeyPressed(Keys.KEY_LEFT)) {
      moveBy += Movement.MOVE_LEFT;
    }
    if (this.keyboard.isKeyPressed(Keys.KEY_D) || this.keyboard.isKeyPressed(Keys.KEY_RIGHT)) {
      moveBy += Movement.MOVE_RIGHT;
    }

    this.moveBy(moveBy);
  }

  private checkTouchDirection(): void {
    let moveBy = 0;
    switch (this.touch.direction) {
      case TouchDirection.NONE:
        break;
      case TouchDirection.UP:
        moveBy += Movement.MOVE_UP;
        break;
      case TouchDirection.DOWN:
        moveBy += Movement.MOVE_DOWN;
        break;
      case TouchDirection.LEFT:
        moveBy += Movement.MOVE_LEFT;
        break;
      case TouchDirection.RIGHT:
        moveBy += Movement.MOVE_RIGHT;
        break;
      case TouchDirection.UP_LEFT:
        moveBy += Movement.MOVE_UP + Movement.MOVE_LEFT;
        break;
      case TouchDirection.DOWN_LEFT:
        moveBy += Movement.MOVE_DOWN + Movement.MOVE_LEFT;
        break;
      case TouchDirection.UP_RIGHT:
        moveBy += Movement.MOVE_UP + Movement.MOVE_RIGHT;
        break;
      case TouchDirection.DOWN_RIGHT:
        moveBy += Movement.MOVE_DOWN + Movement.MOVE_RIGHT;
        break;
    }
    this.moveBy(moveBy);
  }

  public destroy(): void {
    if (this.moveInterval) {
      clearInterval(this.moveInterval);
    }
  }
}
